class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.reset = this.querySelector('.search_drawer_reset');
    this.emptyWrapper = this.querySelector('.empty_search_drawer');
    this.resultWrapper = this.querySelector('.result_search_drawer');
    this.resultInner = this.resultWrapper.querySelector('.result_search_inner_popup');
  }

  connectedCallback() {
    if (this.input) {
      this.input.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));
    }
    this.reset?.addEventListener('click', this.handleReset.bind(this));

    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();

        const q = this.input.value.trim();
        if (q.length > 0) {
          window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(q)}&options[prefix]=last`;
        }
      }
    });
  }

  debounce(fn, delay) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }  

  handleReset(e) {
    e.preventDefault();
    this.input.value = "";
    this.showEmpty(true);
  }

  handleInput() {
    const q = this.input.value.trim();

    if (q.length === 0) {
      this.showEmpty(true);
      return;
    }

    fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page`)
      .then(res => res.json())
      .then(data => this.renderResults(data.resources.results))
      .catch(err => console.error('Error fetching results:', err));
  }

  showEmpty(state) {
    this.emptyWrapper.style.display = state ? "flex" : "none";
    this.resultWrapper.style.display = state ? "none" : "block";
  }

  formatPrice(amount) {
    // Predictive search returns decimal major-unit prices, not integer cents.
    if (amount == null || String(amount).trim() === '') return '';
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';

    if (!this.priceFormatter) {
      // The active-currency fallback also supports cached, older drawer markup.
      const currency = this.dataset.currency || window.Shopify?.currency?.active;
      if (!currency) return '';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.priceFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      });
    }
    return this.priceFormatter.format(value);
  }

  renderResults(results) {
    const { products = [], collections = [], articles = [], pages = [], queries = [] } = results;
    const labels = {
      suggestions: this.dataset.labelSuggestions || 'Suggestions',
      collections: this.dataset.labelCollections || 'Collections',
      products: this.dataset.labelProducts || 'Products',
      pages: this.dataset.labelPages || 'Pages',
      articles: this.dataset.labelArticles || 'Articles',
      readMore: this.dataset.labelReadMore || 'Read more',
    };

    this.showEmpty(false);
    this.resultInner.innerHTML = "";

    const addGroup = (title, html) => {
      const wrap = document.createElement('div');
      wrap.classList.add('search_group');
      wrap.innerHTML = `<h6>${title}</h6>${html}`;
      this.resultInner.appendChild(wrap);
    };

    if (queries.length) {
      addGroup(labels.suggestions, queries.map(q => `
        <div class="query_card">
          <p><a href="${q.url}" aria-label="${q.text}">${q.styled_text}</a></p>
        </div>
      `).join(""));
    }

    if (collections.length) {
      addGroup(labels.collections, collections.map(c => `
        <div class="search_result_collection_card">
          <p><a href="${c.url}" aria-label="${c.title}">${c.title}</a></p>
        </div>
      `).join(""));
    }

    if (products.length) {
      addGroup(labels.products, products.map(p => `
        <div class="search_result_product_card">
          <div class="search_result_product_card_img ratio" style="--aspect-ratio: 90%;">
            <a href="${p.url}" aria-label="${p.title}"><img src="${p.featured_image?.url}" alt="${p.title}"></a>
          </div>
          <div class="search_result_product_card_info">
            <p><a href="${p.url}" aria-label="${p.title}">${p.title}</a></p>
            <div class="search_result_product_price">
              <span><bdi>${this.formatPrice(p.price_min)}</bdi></span>
              ${Number(p.compare_at_price_min) > Number(p.price_min) ? `<s><bdi>${this.formatPrice(p.compare_at_price_min)}</bdi></s>` : ""}
            </div>
          </div>
        </div>
      `).join(""));
    }

    if (pages.length) {
      addGroup(labels.pages, pages.map(pg => `
        <div class="search_result_page_card">
          <p><a href="${pg.url}" aria-label="${pg.title}">${pg.title}</a></p>
        </div>
      `).join(""));
    }

    if (articles.length) {
      addGroup(labels.articles, articles.map(a => `
        <div class="search_result_article_card">
          <div class="search_result_article_card_img ratio" style="--aspect-ratio: 90%;">
            <a href="${a.url}" aria-label="${a.title}"><img src="${a.image}" alt="${a.title}"></a>
          </div>
          <div class="search_result_article_info">
            <p>${a.title}</p>
            <a href="${a.url}" aria-label="${a.title}">${labels.readMore}</a>
          </div>
        </div>
      `).join(""));
    }    

    if (!queries.length && !collections.length && !products.length && !articles.length && !pages.length) {
      const continueUrl = this.dataset.continueUrl || `${window.Shopify.routes.root}collections/women-s-perfumes`;
      const continueLabel = this.dataset.continueLabel || 'Continue shopping';
      const noResults = this.dataset.noResults || 'No results found. Please try a different query.';
      this.resultInner.innerHTML = `
        <div class="search-drawer-empty">
          <h6 class="empty_results_heading">${noResults}</h6>
          <a class="button button-style-filled btn-prime" href="${continueUrl}">${continueLabel}</a>
        </div>
      `;
    }
  }
}

customElements.define('search-drawer', SearchDrawer);
